/**
 * This is matching logic from the backend calulations for clearing sale billing documents.
 * It will soon be replaced by upcoming clearning sale billing run work.
 */

interface DeploymentSale {
  deployment_id: string;
  default_buyers_premium?: number;
  [key: string]: unknown;
}

interface Deployment {
  is_gst_registered?: boolean;
  [key: string]: unknown;
}

type Lookup<T> = Record<string, T>;

interface LotCalculationContext {
  deploymentSale: DeploymentSale | null;
  deployment: Deployment | null;
  isDeploymentGstRegistered: boolean;
  chargeGst: boolean;
}

/**
 * Get the calculation context for a sale lot (deployment, GST flags)
 */
function getLotCalculationContext(
  saleLot: SaleLot,
  chargeGst: boolean,
  deploymentSalesBySaleLotIdLookup: Lookup<DeploymentSale>,
  deployments: Lookup<Deployment>,
  isDeploymentGstRegistered: boolean,
): LotCalculationContext {
  const deploymentSale = deploymentSalesBySaleLotIdLookup[saleLot.id] || null;
  const deployment = deploymentSale
    ? deployments[deploymentSale.deployment_id] || null
    : null;

  return {
    deploymentSale,
    deployment,
    isDeploymentGstRegistered,
    chargeGst,
  };
}

/**
 * Calculate GST on an amount (10%)
 */
function calculateGst(amountCents: number): number {
  return Math.round(amountCents * 0.1);
}

/**
 * Calculate buyers premium for a sale lot (returns cents)
 */
function calculateBuyersPremium(
  saleLot: SaleLot,
  salePriceCents: number,
  deploymentSale: DeploymentSale | null,
): number {
  const defaultBuyersPremium = deploymentSale?.default_buyers_premium || 0;
  const effectiveBuyersPremiumDecimal =
    saleLot.buyersPremium !== null && saleLot.buyersPremium !== undefined
      ? saleLot.buyersPremium
      : defaultBuyersPremium;

  // Match backend calculate_percentage: convert to dollars, multiply by percentage,
  // round to nearest cent
  const salePriceDollars = salePriceCents / 100;
  const premiumDollars = salePriceDollars * effectiveBuyersPremiumDecimal;
  const premiumCents = Math.round(premiumDollars * 100);
  return premiumCents;
}

export const calculateBuyerInvoiceTotal = (
  saleLot: SaleLot,
  chargeGst: boolean,
  deploymentSalesBySaleLotIdLookup: Lookup<DeploymentSale>,
  deployments: Lookup<Deployment>,
  isDeploymentGstRegistered: boolean,
): number => {
  let totalCents = 0;
  if (!saleLot || !saleLot.buyer_id) {
    return totalCents;
  }

  const context = getLotCalculationContext(
    saleLot,
    chargeGst,
    deploymentSalesBySaleLotIdLookup,
    deployments,
    isDeploymentGstRegistered,
  );

  const salePriceCents = saleLot.total_price_cents || 0;

  // Sale price
  totalCents += salePriceCents;

  // Sale price GST (10% if vendor GST applicable)
  if (context.chargeGst) {
    totalCents += calculateGst(salePriceCents);
  }

  // Buyers premium
  const buyersPremiumCents = calculateBuyersPremium(
    saleLot,
    salePriceCents,
    context.deploymentSale,
  );
  totalCents += buyersPremiumCents;

  // Buyers premium GST (10% if deployment is GST registered)
  if (context.isDeploymentGstRegistered) {
    totalCents += calculateGst(buyersPremiumCents);
  }

  return totalCents;
};
